import type { ComponentType } from "react";
import {
  IconActivity,
  IconAlertCircle,
  IconBrush,
  IconCloudComputing,
  IconCoin,
  IconDashboard,
  IconDeviceDesktop,
  IconHeadset,
  IconHome,
  IconHourglass,
  IconMessageCircle,
  IconReportAnalytics,
  IconSettings,
  IconShoppingCart,
  IconTool,
  IconTools,
} from "@tabler/icons-react";

export interface NavLeaf {
  path: string;
  label: string;
  icon: ComponentType<any>;
}

export interface NavEntry extends NavLeaf {
  children?: NavLeaf[];
}

/** The one source of truth for navigation: sidebar, mobile menu and page titles. */
export const NAV_ITEMS: NavEntry[] = [
  { path: "/home", label: "Home", icon: IconHome },
  { path: "/status", label: "Status", icon: IconDashboard },
  { path: "/kiosks", label: "Kiosks", icon: IconDeviceDesktop },
  {
    path: "/maintenance",
    label: "Maintenance",
    icon: IconTools,
    children: [
      { path: "/maintenance/open-issues", label: "Open issues", icon: IconAlertCircle },
      { path: "/maintenance/cleaners", label: "Cleaners", icon: IconBrush },
      { path: "/maintenance/engineers", label: "Engineer visits", icon: IconTool },
    ],
  },
  {
    path: "/monitoring",
    label: "Monitoring",
    icon: IconActivity,
    children: [
      { path: "/monitoring/open-incidents", label: "Open incidents", icon: IconAlertCircle },
      { path: "/monitoring/degraded-monitors", label: "Degraded monitors", icon: IconActivity },
    ],
  },
  {
    path: "/customer-service",
    label: "Customer service",
    icon: IconMessageCircle,
    children: [
      { path: "/customer-service/sales", label: "Sales", icon: IconShoppingCart },
      { path: "/customer-service/helpdesk", label: "Helpdesk", icon: IconHeadset },
    ],
  },
  {
    path: "/reports",
    label: "Reports",
    icon: IconReportAnalytics,
    children: [
      { path: "/reports/overview", label: "Overview", icon: IconReportAnalytics },
      { path: "/reports/sales-by-store", label: "Sales by store", icon: IconCoin },
      { path: "/reports/machines-left-complete", label: "Machines left complete", icon: IconHourglass },
      { path: "/reports/errors", label: "Errors", icon: IconAlertCircle },
      { path: "/reports/downtime", label: "Downtime", icon: IconActivity },
    ],
  },
  { path: "/proxy-health", label: "Alliance", icon: IconCloudComputing },
  { path: "/setting", label: "Settings", icon: IconSettings },
];

/** Thumb-reach shortcuts on phones: the screens floor staff use most. */
export const BOTTOM_NAV: NavLeaf[] = [
  { path: "/home", label: "Home", icon: IconHome },
  { path: "/status", label: "Status", icon: IconDashboard },
  { path: "/maintenance/open-issues", label: "Issues", icon: IconAlertCircle },
  { path: "/customer-service/helpdesk", label: "Helpdesk", icon: IconHeadset },
];

export function isPathActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function pageTitleFor(pathname: string): string {
  if (isPathActive(pathname, "/proxy-health/commands")) return "Command history";
  for (const item of NAV_ITEMS) {
    const child = item.children?.find((c) => isPathActive(pathname, c.path));
    if (child) return child.label;
    if (isPathActive(pathname, item.path)) return item.label;
  }
  return "Speed Queen";
}
